import React, {useState} from 'react';
import {useLocation, useNavigate, Link} from "react-router-dom";
import {Alert, Form, Button} from 'react-bootstrap';
import {QRCodeSVG} from 'qrcode.react';
import * as formik from 'formik';
import * as yup from 'yup';
import {useAuth} from "../../contexts/AuthContext.jsx";
import {adminAuthApi} from "../../api/adminAuth.js";

const apiErrorMessage = (error, fallback) =>
    error?.message || error?.err?.response?.data?.error?.errorDescription ||
    error?.response?.data?.error?.errorDescription || fallback;

function LoginPage() {
    const { Formik } = formik;
    const schema = yup.object().shape({
        email: yup.string().required(),
        password: yup.string().required(),
    });
    const [pageError, setPageError] = useState(null);
    const [mfaFlow, setMfaFlow] = useState(null);
    const [mfaCode, setMfaCode] = useState('');
    const [recoveryCode, setRecoveryCode] = useState('');
    const [useRecoveryCode, setUseRecoveryCode] = useState(false);
    const [submittingMfa, setSubmittingMfa] = useState(false);
    const [enrollmentResult, setEnrollmentResult] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const authContext = useAuth();
    const from = location.state?.from?.pathname || "/";

    const resetMfaFlow = () => {
        setMfaFlow(null);
        setMfaCode('');
        setRecoveryCode('');
        setUseRecoveryCode(false);
        setEnrollmentResult(null);
        setPageError(null);
    };

    const submitMfa = async (event) => {
        event.preventDefault();
        setSubmittingMfa(true);
        setPageError(null);
        try {
            if (mfaFlow.enrollmentRequired) {
                const result = await adminAuthApi.confirmMfaEnrollment(mfaFlow.challengeToken, mfaCode);
                setEnrollmentResult(result);
            } else {
                const result = await adminAuthApi.verifyMfa(
                    mfaFlow.challengeToken,
                    useRecoveryCode ? null : mfaCode,
                    useRecoveryCode ? recoveryCode : null
                );
                authContext.completeMfaSignIn(result);
                navigate(from, {replace: true});
            }
        } catch (error) {
            setPageError(apiErrorMessage(error, 'The authenticator code could not be verified.'));
        } finally {
            setSubmittingMfa(false);
        }
    };

    if (enrollmentResult) {
        return (
            <div className="w-100">
                <h4 className="text-secondary">Save your recovery codes</h4>
                <hr className="border-secondary mb-4"/>
                <Alert variant="warning">
                    Store these codes somewhere safe. Each code can be used once if you lose access to your authenticator app.
                </Alert>
                <div className="border rounded bg-light p-3 mb-3 font-monospace">
                    {enrollmentResult.recoveryCodes.map(code => <div key={code}>{code}</div>)}
                </div>
                <Button className="btn-primary w-100" onClick={() => {
                    authContext.completeMfaSignIn(enrollmentResult.authentication);
                    navigate(from, {replace: true});
                }}>
                    I have saved these codes
                </Button>
            </div>
        );
    }

    if (mfaFlow) {
        return (
            <Form onSubmit={submitMfa} className="w-100">
                <h4 className="text-secondary">
                    {mfaFlow.enrollmentRequired ? 'Set up multi-factor authentication' : 'Enter authenticator code'}
                </h4>
                <hr className="border-secondary mb-4"/>

                {mfaFlow.enrollmentRequired && (
                    <>
                        <p>Scan this QR code using your authenticator app.</p>
                        <div className="d-flex justify-content-center bg-white p-3 mb-3">
                            <QRCodeSVG value={mfaFlow.enrollment.authenticatorUri} size={220}/>
                        </div>
                        <p className="small text-muted mb-1">If you cannot scan it, enter this setup key manually:</p>
                        <div className="border rounded bg-light p-2 mb-3 text-center font-monospace text-break">
                            {mfaFlow.enrollment.sharedKey}
                        </div>
                    </>
                )}

                {!mfaFlow.enrollmentRequired && (
                    <Button variant="link" className="p-0 mb-3" type="button"
                            onClick={() => { setUseRecoveryCode(!useRecoveryCode); setPageError(null); }}>
                        {useRecoveryCode ? 'Use an authenticator code' : 'Use a recovery code instead'}
                    </Button>
                )}

                {useRecoveryCode ? (
                    <Form.Group className="mb-3" controlId="recoveryCode">
                        <Form.Label>Recovery code</Form.Label>
                        <Form.Control value={recoveryCode} onChange={event => setRecoveryCode(event.target.value)}
                                      autoComplete="one-time-code" required autoFocus/>
                    </Form.Group>
                ) : (
                    <Form.Group className="mb-3" controlId="mfaCode">
                        <Form.Label>Six-digit code</Form.Label>
                        <Form.Control value={mfaCode}
                                      onChange={event => setMfaCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                                      inputMode="numeric" pattern="[0-9]{6}" autoComplete="one-time-code"
                                      placeholder="123456" required autoFocus/>
                    </Form.Group>
                )}

                <Button className="btn-primary w-100" type="submit" disabled={submittingMfa}>
                    {mfaFlow.enrollmentRequired ? 'Verify and enable MFA' : 'Verify'}
                </Button>
                <Button variant="link" className="w-100 mt-2" type="button" onClick={resetMfaFlow}>
                    Back to login
                </Button>
                {pageError && <Alert variant="danger" className="mt-3">{pageError}</Alert>}
            </Form>
        );
    }

    return (
        <Formik
            validationSchema={schema}
            onSubmit={async (values, {setSubmitting}) => {
                setPageError(null);
                try {
                    const result = await authContext.signIn(values.email, values.password);
                    if (result.mfaEnrollmentRequired) {
                        const enrollment = await adminAuthApi.beginMfaEnrollment(result.mfaChallengeToken);
                        setMfaFlow({challengeToken: result.mfaChallengeToken, enrollmentRequired: true, enrollment});
                    } else if (result.mfaRequired) {
                        setMfaFlow({challengeToken: result.mfaChallengeToken, enrollmentRequired: false});
                    } else {
                        navigate(from, {replace: true});
                    }
                } catch (error) {
                    setPageError(apiErrorMessage(error, 'Invalid username or password.'));
                } finally {
                    setSubmitting(false);
                }
            }}
            initialValues={{email: '', password: ''}}
        >
            {({handleSubmit, handleChange, touched, values, isSubmitting, errors}) => (
                <Form noValidate onSubmit={handleSubmit} className="w-100">
                    <h4 className="text-secondary">Admin Login</h4>
                    <hr className="border-secondary mb-5"/>
                    <Form.Group className="mb-3" controlId="email">
                        <Form.Label>Username</Form.Label>
                        <Form.Control name="email" onChange={handleChange} value={values.email}
                                      type="text" placeholder="Enter username or email"
                                      autoComplete="username" isInvalid={touched.email && !!errors.email}/>
                        <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-1" controlId="password">
                        <Form.Label>Password</Form.Label>
                        <Form.Control name="password" onChange={handleChange} value={values.password}
                                      type="password" placeholder="Password" autoComplete="current-password"
                                      isInvalid={touched.password && !!errors.password}/>
                        <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Link className="small text-info" to="/forgotPassword">Forgot password?</Link>
                    </Form.Group>
                    <Button className="btn-primary w-100 mt-3" type="submit" disabled={isSubmitting}>Login</Button>
                    {pageError && <Alert variant="danger" className="mt-3">{pageError}</Alert>}
                </Form>
            )}
        </Formik>
    );
}

export default LoginPage;
